import { useCallback, useEffect, useState } from "react";
import {
  createRole,
  deleteRole,
  fetchRoles,
  replaceRolePermissions,
  updateRole,
} from "../../api/access";
import ReasonModal from "../../component/access/ReasonModal";
import { useAccess } from "../../context/AccessContext";
import "../../css/AccessManagement.css";
import { getAccessErrorMessage } from "../../utils/accessErrors";
import Layout from "../Layout";
import { useCatalog } from "./accessData";

const ROLE_KEY_PATTERN = /^[a-z][a-z0-9_]{1,59}$/; // API ROLE_KEY_PATTERN 과 같다
const EMPTY_FORM = { roleKey: "", name: "", description: "" };

/**
 * 권한 관리 — 역할 편집 (spec R-MGMT-07/08, R-FE-06).
 * 역할 생성·이름/설명 수정·삭제(soft)·권한 교체. 시스템 역할(isSystem)은 삭제 버튼을 숨긴다(권한 편집은 가능).
 */
function AccessRoles() {
  const { hasPermission, refreshAccess } = useAccess();
  const canManage = hasPermission("access.permission.manage");
  const { catalog, hiddenKeys } = useCatalog();
  // 숨김 키는 화면에 안 보이고 저장에도 보내지 않는다(역할이 이미 가진 숨김 키는 서버가 보존한다)
  const visibleKeys = useCallback((keys) => (keys ?? []).filter((k) => !hiddenKeys.has(k)), [hiddenKeys]);
  const openPermissions = (role) =>
    setEditPerms({ roleKey: role.roleKey, name: role.name, keys: visibleKeys(role.permissionKeys) });

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createForm, setCreateForm] = useState(null); // EMPTY_FORM | null
  const [editInfo, setEditInfo] = useState(null); // { roleKey, name, description }
  const [editPerms, setEditPerms] = useState(null); // { roleKey, name, keys: string[] }
  const [modal, setModal] = useState(null); // { type: 'create'|'update'|'delete'|'permissions', role? }

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRoles(await fetchRoles());
    } catch (err) {
      setError(getAccessErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const afterChange = async () => {
    setModal(null);
    await load();
    await refreshAccess(); // 내 역할에 영향이 있을 수 있다
  };

  const confirm = async (reason) => {
    if (modal.type === "create") {
      await createRole({ ...createForm, reason });
      setCreateForm(null);
    } else if (modal.type === "update") {
      await updateRole(editInfo.roleKey, { name: editInfo.name, description: editInfo.description, reason });
      setEditInfo(null);
    } else if (modal.type === "delete") {
      await deleteRole(modal.role.roleKey, reason);
    } else if (modal.type === "permissions") {
      await replaceRolePermissions(editPerms.roleKey, { permissionKeys: visibleKeys(editPerms.keys), reason });
      setEditPerms(null);
    }
    await afterChange();
  };

  const togglePermission = (key) =>
    setEditPerms((prev) => ({
      ...prev,
      keys: prev.keys.includes(key) ? prev.keys.filter((k) => k !== key) : [...prev.keys, key],
    }));

  const createInvalid =
    !createForm || !ROLE_KEY_PATTERN.test(createForm.roleKey) || !createForm.name.trim() || createForm.name.length > 100;

  const modalTitle = {
    create: "역할 생성",
    update: "역할 수정",
    delete: "역할 삭제",
    permissions: "역할 권한 변경",
  }[modal?.type];

  const modalSummary = () => {
    if (!modal) return null;
    if (modal.type === "create") return `${createForm.name} (${createForm.roleKey}) 역할을 만듭니다.`;
    if (modal.type === "update") return `${editInfo.roleKey} 역할의 이름·설명을 바꿉니다.`;
    if (modal.type === "delete")
      return `${modal.role.name} (${modal.role.roleKey}) 역할을 삭제합니다. 소속 ${modal.role.memberCount}명은 이 역할로 받은 권한을 잃습니다.`;
    const before = visibleKeys(roles.find((r) => r.roleKey === editPerms.roleKey)?.permissionKeys);
    const after = visibleKeys(editPerms.keys);
    const added = after.filter((k) => !before.includes(k));
    const removed = before.filter((k) => !after.includes(k));
    return (
      <>
        <div>
          {editPerms.name} ({editPerms.roleKey})
        </div>
        <div>추가: {added.join(", ") || "없음"}</div>
        <div>제거: {removed.join(", ") || "없음"}</div>
      </>
    );
  };

  return (
    <Layout>
      <div className="access-page">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h4 className="access-page-title">권한 관리 · 역할 편집</h4>
            <p className="access-page-desc">역할은 권한의 묶음입니다. 한 어드민이 여러 역할을 가질 수 있습니다.</p>
          </div>
          {canManage && !createForm && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreateForm(EMPTY_FORM)}>
              역할 추가
            </button>
          )}
        </div>

        {!canManage && (
          <div className="access-readonly-note">조회 전용입니다. 변경하려면 '권한 관리 변경' 권한이 필요합니다.</div>
        )}
        {error && <div className="access-error">{error}</div>}

        {canManage && createForm && (
          <div className="access-section">
            <div className="access-domain-title" style={{ marginTop: 0 }}>
              새 역할
            </div>
            <div className="access-toolbar">
              <input
                className="form-control form-control-sm"
                placeholder="역할 키 (예: cs_team)"
                value={createForm.roleKey}
                onChange={(e) => setCreateForm({ ...createForm, roleKey: e.target.value.trim() })}
              />
              <input
                className="form-control form-control-sm"
                placeholder="이름"
                maxLength={100}
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              />
              <input
                className="form-control form-control-sm"
                placeholder="설명 (선택)"
                maxLength={255}
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
              <button type="button" className="btn btn-light btn-sm" onClick={() => setCreateForm(null)}>
                취소
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={createInvalid}
                onClick={() => setModal({ type: "create" })}
              >
                생성
              </button>
            </div>
            <div className="access-muted" style={{ marginTop: 6 }}>
              역할 키는 영문 소문자로 시작하고 소문자·숫자·밑줄만 쓸 수 있습니다(2~60자). 생성 후에는 바꿀 수 없습니다.
            </div>
          </div>
        )}

        <div className="access-section access-table-wrap">
          {loading ? (
            <div className="access-empty">불러오는 중...</div>
          ) : roles.length === 0 ? (
            <div className="access-empty">역할이 없습니다</div>
          ) : (
            <table className="table table-hover access-table">
              <thead>
                <tr>
                  <th>역할</th>
                  <th>설명</th>
                  <th>권한 수</th>
                  <th>소속 인원</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => {
                  const editingThis = editInfo?.roleKey === role.roleKey;
                  return (
                    <tr key={role.roleKey} data-testid={`role-row-${role.roleKey}`}>
                      <td>
                        {editingThis ? (
                          <input
                            className="form-control form-control-sm"
                            maxLength={100}
                            value={editInfo.name}
                            onChange={(e) => setEditInfo({ ...editInfo, name: e.target.value })}
                          />
                        ) : (
                          <strong>{role.name}</strong>
                        )}
                        <div className="access-muted">
                          {role.roleKey}{" "}
                          {role.isSystem && <span className="access-badge access-badge-system">시스템</span>}
                        </div>
                      </td>
                      <td>
                        {editingThis ? (
                          <input
                            className="form-control form-control-sm"
                            maxLength={255}
                            value={editInfo.description}
                            onChange={(e) => setEditInfo({ ...editInfo, description: e.target.value })}
                          />
                        ) : (
                          role.description || <span className="access-muted">-</span>
                        )}
                      </td>
                      <td>{visibleKeys(role.permissionKeys).length}</td>
                      <td>{role.memberCount}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {canManage &&
                          (editingThis ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-light btn-sm me-1"
                                onClick={() => setEditInfo(null)}
                              >
                                취소
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={!editInfo.name.trim()}
                                onClick={() => setModal({ type: "update" })}
                              >
                                저장
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm me-1"
                                onClick={() =>
                                  setEditInfo({
                                    roleKey: role.roleKey,
                                    name: role.name,
                                    description: role.description ?? "",
                                  })
                                }
                              >
                                수정
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm me-1"
                                onClick={() => openPermissions(role)}
                              >
                                권한 편집
                              </button>
                              {!role.isSystem && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => setModal({ type: "delete", role })}
                                >
                                  삭제
                                </button>
                              )}
                            </>
                          ))}
                        {!canManage && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => openPermissions(role)}
                          >
                            권한 보기
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {editPerms && (
          <div className="access-section" data-testid="role-permission-editor">
            <div className="d-flex justify-content-between align-items-center">
              <strong>
                {editPerms.name} ({editPerms.roleKey}) 권한
              </strong>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-light btn-sm" onClick={() => setEditPerms(null)}>
                  닫기
                </button>
                {canManage && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setModal({ type: "permissions" })}>
                    권한 저장
                  </button>
                )}
              </div>
            </div>
            {catalog.map((group) => (
              <div key={group.domain}>
                <div className="access-domain-title">{group.domain}</div>
                <div className="access-check-grid">
                  {group.permissions.map((p) => (
                    <label key={p.key} title={p.key}>
                      <input
                        type="checkbox"
                        checked={editPerms.keys.includes(p.key)}
                        disabled={!canManage}
                        onChange={() => togglePermission(p.key)}
                      />
                      <span>
                        {p.description} <span className="access-muted">({p.key})</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ReasonModal
        isOpen={Boolean(modal)}
        title={modalTitle}
        danger={modal?.type === "delete"}
        confirmLabel={modal?.type === "delete" ? "삭제" : "변경 저장"}
        summary={modalSummary()}
        onConfirm={confirm}
        onClose={() => setModal(null)}
      />
    </Layout>
  );
}

export default AccessRoles;
